import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DataService } from "./data.service";
import { LoginUser } from "../model/login-user";
import { SectorFilter } from "../model/sector-filter";
import { Center } from "../model/center";
import { ServiceFilter } from "../model/service-filter";
import { Scanner } from "../model/scanner/scanner";
import { UserService } from "./user.service";
import { ServiceService } from "./service.service";
import { environment } from "../../environments/environment";
import { NotificationToastService } from "./notification-toast.service";
import { SectorType } from "../enums/sector-type.enum";
import { CompanyFilter } from "../model/company-filter";
import { isValidPasswordFormat } from "../validators/password/password.validator";
import { SystemConfigurationService } from "./system-configuration/system-configuration.service";
import { SystemConfiguration } from "../model/system-configuration/system-configuration";
import { BehaviorSubject } from "rxjs";
import { filter, take } from "rxjs/operators";

@Injectable()
export class AuthService {
  serverVersion = "";
  private loginUser: LoginUser;
  private loggedIn = false;
  private scanner: Scanner;
  private menuTags: string[];

  constructor(
    private userService: UserService,
    private scService: SystemConfigurationService,
    private serviceService: ServiceService,
    private dataService: DataService,
  ) {}

  private refreshInProgress = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  private readonly loginsEndPoint: string = "/authentication/login/";
  private readonly refreshTokenEndPoint: string =
    "/authentication/RefreshToken/";
  private readonly changePasswordEndPoint: string =
    "/authentication/changePassword/";

  async changePassword(
    userId: number,
    actualPassword: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<boolean> {
    const changePass = {
      UserId: userId,
      ActualPassword: actualPassword,
      NewPassword: newPassword,
      ConfirmPassword: confirmPassword,
    };

    const reponse = await this.dataService
      .post<boolean>(this.changePasswordEndPoint, changePass)
      .toPromise();

    localStorage.setItem("change-pass", "0");
    localStorage.removeItem("change-pass");

    return reponse;
  }

  getValidToken(): Promise<string> {
    if (!this.isTokenRefreshRequired()) {
      return Promise.resolve(this.getToken());
    }
    return this.refreshToken();
  }

  async refreshTokenIfRequired(): Promise<string> {
    if (!this.isTokenRefreshRequired()) {
      return this.getToken();
    }

    if (this.refreshInProgress) {
      return new Promise((resolve) => {
        this.refreshSubject
          .pipe(
            filter((t) => t !== null),
            take(1),
          )
          .subscribe(resolve);
      });
    }

    this.refreshInProgress = true;

    try {
      const token = await this.refreshToken();
      this.refreshSubject.next(token);
      return token;
    } finally {
      this.refreshInProgress = false;
    }
  }

  async refreshToken(): Promise<string> {
    const tokenResponse = await this.dataService
      .post<any>(this.refreshTokenEndPoint, {})
      .toPromise();

    localStorage.setItem("token", tokenResponse.Token);

    const expiresAt =
      Date.now() + tokenResponse.ExpirationTokenMinutes * 60 * 1000;

    localStorage.setItem("token_expires_at", expiresAt.toString());

    return tokenResponse.Token;
  }

  async login(userName: string, userPassword: string): Promise<boolean> {
    const login = { userName: userName, userPassword: userPassword };
    this.loginUser = await this.dataService
      .post<LoginUser>(this.loginsEndPoint, login)
      .toPromise();
    this.menuTags = this.loginUser.MenuTags;
    this.setServerVersion(this.loginUser.Version);

    if (!isValidPasswordFormat(userPassword)) {
      localStorage.setItem("change-pass", "1");
    }

    return this.loginUser.Token != null;
  }

  isValidMenuTag(tag: string): boolean {
    const menuCache = localStorage.getItem("menus");
    if (!this.menuTags && menuCache) {
      this.menuTags = <string[]>JSON.parse(menuCache);
    }

    if (this.menuTags) {
      return this.menuTags.findIndex((m) => m === tag) > -1;
    } else {
      return false;
    }
  }

  isExtraccionistaModality(): boolean {
    return localStorage.getItem("extaccionista-modality") === "1";
  }

  getInitUrl() {
    if (this.isExtraccionistaModality()) {
      return "/extraccionista-search";
    } else {
      return "/home";
    }
  }

  isNumber(value: string | number): boolean {
    return !isNaN(Number(value.toString()));
  }

  async canViewControlOrdenAttachedFile(): Promise<boolean> {
    const configuration = await this.scService.getSystemConfiguration();
    return configuration.CanViewControlOrdenAttachedFile;
  }

  async canViewMedicalPatientInfo(): Promise<boolean> {
    const configuration = await this.scService.getSystemConfiguration();
    return configuration.CanViewMedicalPatientInfo;
  }

  async canViewMedicaOrderDate(): Promise<boolean> {
    const configuration = await this.scService.getSystemConfiguration();
    return configuration.CanViewMedicaOrderDate;
  }

  async canViewReferringPhysician(): Promise<boolean> {
    const configuration = await this.scService.getSystemConfiguration();
    return configuration.CanViewReferringPhysician;
  }

  async canViewStudyReportRegion(): Promise<boolean> {
    const configuration = await this.scService.getSystemConfiguration();
    return configuration.CanViewStudyReportRegion;
  }

  async OnlyViewHc(): Promise<boolean> {
    const configuration = await this.scService.getSystemConfiguration();
    return configuration.OnlyViewHc;
  }

  getCompanyIds(): number[] {
    const companyIds: number[] = [];
    let items = localStorage.getItem("companyIds");
    items = items.replace("[", "");
    items = items.replace("]", "");

    const companyIdsSplitted = items.split(",");
    companyIdsSplitted.forEach((s) => {
      let id = s.replace("[", "");
      id = id.replace("]", "");
      if (this.isNumber(id)) {
        companyIds.push(+id);
      }
    });
    return companyIds;
  }

  getServiceIds(): number[] {
    const serviceIds: number[] = [];
    const serviceIdsSplitted = localStorage.getItem("serviceIds").split(",");
    serviceIdsSplitted.forEach((s) => {
      let id = s.replace("[", "");
      id = id.replace("]", "");
      if (this.isNumber(id)) {
        serviceIds.push(+id);
      }
    });

    return serviceIds;
  }

  async getUserScanner(): Promise<Scanner> {
    if (!this.scanner) {
      // + --> lo convierte en numerico
      this.scanner = await this.userService.getScannersByUserId(
        +localStorage.getItem("id"),
      );
    }
    return this.scanner;
  }

  setUserScanner(scanner: Scanner) {
    this.scanner = scanner;
  }

  getUserId(): number {
    return +localStorage.getItem("id");
  }

  getCenterId(): number {
    return +localStorage.getItem("centerId");
  }

  getSectorTypeId(): number {
    return +localStorage.getItem("sector-type-id");
  }

  getSectorScannDocument(): boolean {
    return localStorage.getItem("sector-scann-document") === "1";
  }

  getSectorId(): number {
    return +localStorage.getItem("sectorId");
  }

  getLoginUser(): LoginUser {
    return this.loginUser;
  }

  getTxControlUrl(): string | null {
    return localStorage.getItem("tx-control-url");
  }

  getBillingCloseDate(): Date | null {
    const billingCloseDateStr = localStorage.getItem('BillingCloseDate');
    if (billingCloseDateStr && billingCloseDateStr.length > 0) {
      return new Date(billingCloseDateStr);
    }
    return null;
  }

  txControlEnabled(): boolean {
    return this.getTxControlUrl() &&
      this.getTxControlUrl() !== 'null' &&
      this.getTxControlUrl().length > 0;
  }

  getCardName(): string {
    return localStorage.getItem("card-name");
  }

  getUserName(): string {
    return localStorage.getItem("user-name");
  }

  getMobileLinkId(): string {
    return localStorage.getItem("mobile-link-id");
  }

  getEnvironment(): string {
    return localStorage.getItem("environment");
  }

  changePassRequired(): boolean {
    return localStorage.getItem("change-pass") === "1";
  }

  seeDraftReservationCalendar(): boolean {
    return localStorage.getItem("see-draft-reservation-calendar") === "1";
  }

  receiveMedicalAppointmentWaitNotification(): boolean {
    return (
      localStorage.getItem("medical-appointment-wait-notification") === "1"
    );
  }

  seeCloseWithTotals(): boolean {
    return localStorage.getItem("see-close-with-totals") === "1";
  }

  seePatientContactInfo(): boolean {
    return localStorage.getItem("see-patient-contact-info") === "1";
  }

  enabledIA(): boolean {
    return localStorage.getItem("EnabledIA") === "1";
  }

  enableImportImageKeys(): boolean {
    return localStorage.getItem("EnableImportImageKeys") === "1";
  }

  canCancelAdmited(): boolean {
    return localStorage.getItem("can-cancel-admited") === "1";
  }

  canSendStudyMail(): boolean {
    return localStorage.getItem("can-send-study-mail") === "1";
  }

  canPublishForced(): boolean {
    return localStorage.getItem("can-publish-forced") === "1";
  }

  canViewOtherCentersReception(): boolean {
    return localStorage.getItem("can-view-other-centers-reception") === "1";
  }

  canUpdateLikeParticular(): boolean {
    return localStorage.getItem("can-update-like-particular") === "1";
  }

  canChangeAppointmentDate(): boolean {
    return localStorage.getItem("can-change-appointment-date") === "1";
  }

  canPublish(): boolean {
    return localStorage.getItem("can-publish") === "1";
  }

  seeAllStudies(): boolean {
    return localStorage.getItem("see-all-studies") === "1";
  }

  seeAdmissionValorizationAmount(): boolean {
    return localStorage.getItem("see-admission-valorization-amount") === "1";
  }

  canViewPatientOtherProfesional(): boolean {
    return localStorage.getItem("can-view-patient-other-profesional") === "1";
  }

  CanCloseInvoiceIntegrated(): boolean {
    return localStorage.getItem("CanCloseInvoiceIntegrated") === "1";
  }

  canSendOrderWhatsapp(): boolean {
    return localStorage.getItem("can-send-orderwhatsapp") === "1";
  }

  canSelectAllInvoicePoint(): boolean {
    return localStorage.getItem("can-select-all-invoice-point") === "1";
  }

  filterPhysicianInReservation(): boolean {
    return localStorage.getItem("filter-physician-in-reservation") === "1";
  }

  filterPhysicianInPatient(): boolean {
    return localStorage.getItem("filter-physician-in-reservation") === "1";
  }

  addAutomaticPhysicianInPatient(): boolean {
    return localStorage.getItem("filter-physician-in-reservation") === "1";
  }

  canNoValidateOnLine(): boolean {
    return localStorage.getItem("can-no-validate-on-line") === "1";
  }

  canCreatePendingReservation(): boolean {
    return localStorage.getItem("can-create-pending-reservation") === "1";
  }

  canSeeNoRulesRestriction(): boolean {
    return localStorage.getItem("can-see-no-rules") === "1";
  }

  canTest(): boolean {
    return localStorage.getItem("can-test") === "1";
  }

  validatePhoneRequired(): boolean {
    return localStorage.getItem("ValidatePhoneRequired") === "1";
  }

  canSeeNoRules(): boolean {
    return localStorage.getItem("can-see-no-all-rules") === "1";
  }

  canChangeReservationSessions(): boolean {
    return localStorage.getItem("can-change-reservation-sessions") === "1";
  }

  canCreatePrescription(): boolean {
    return localStorage.getItem("can-create-prescription") === "1";
  }

  canReservationReschedule(): boolean {
    return localStorage.getItem("CanReservationReschedule") === "1";
  }

  myStudiesChecked(): boolean {
    return localStorage.getItem("sector-my-studies-checked") === "1";
  }

  isStudyReportCircuitWord(): boolean {
    return localStorage.getItem("IsStudyReportCircuitWord") === "1";
  }

  controlAdminReport(): boolean {
    return localStorage.getItem("control-admin-report") === "1";
  }

  getStudyReportCircuitId(): string {
    return localStorage.getItem("StudyReportCircuitId");
  }

  getPhysicianId(): number {
    const id = localStorage.getItem("physicianId");
    return id ? +localStorage.getItem("physicianId") : null;
  }

  isOverMedicalAppointmentAth(): boolean {
    const value = localStorage.getItem("overMedicalAppointmentAuth");
    return value === "1";
  }

  getPacsName(): string {
    return localStorage.getItem("pacs-name");
  }

  getSettingsUrl(): string {
    return localStorage.getItem("settings-url");
  }

  getToken(): string {
    return localStorage.getItem("token");
  }

  getInternalServerUrl(): string {
    return localStorage.getItem("internal-server-url");
  }

  getMobileScannApi(): string {
    return localStorage.getItem("mobile-scann-api");
  }

  async getSectorServices(): Promise<ServiceFilter[]> {
    return await this.serviceService.getServicesFilter(this.getSectorId());
  }

  isBillingSector(): boolean {
    return this.getSectorTypeId() === SectorType.Billing;
  }

  setSectorEquipment(): boolean {
    return localStorage.getItem("SetSectorEquipment") === "1";
  }

  getSector(): string {
    return localStorage.getItem("sector");
  }

  getCenter(): string {
    return localStorage.getItem("center");
  }

  getFullName(): string {
    return (
      localStorage.getItem("last-name") +
      ", " +
      localStorage.getItem("first-name")
    );
  }

  isReceptionSector(): boolean {
    return this.getSectorTypeId() === SectorType.Reception;
  }

  logOut() {
    localStorage.removeItem("sector");
    localStorage.removeItem("center");
    localStorage.removeItem("token");
    localStorage.removeItem("token_expires_at");
    localStorage.removeItem("pacs-name");
    localStorage.removeItem("user-name");
    localStorage.removeItem("mobile-link-id");
    localStorage.removeItem("can-test");
    localStorage.removeItem("ValidatePhoneRequired");
    localStorage.removeItem("environment");
    localStorage.removeItem("first-name");
    localStorage.removeItem("last-name");
    localStorage.removeItem("sectorId");
    localStorage.removeItem("sector-type-id");
    localStorage.removeItem("sector-scann-document");
    localStorage.removeItem("serviceIds");
    localStorage.removeItem("centerId");
    localStorage.removeItem("overMedicalAppointmentAuth");
    localStorage.removeItem("companyIds");
    localStorage.removeItem("can-create-pending-reservation");
    localStorage.removeItem("can-change-reservation-sessions");
    localStorage.removeItem("can-create-prescription");
    localStorage.removeItem("physicianId");
    localStorage.removeItem("IsStudyReportCircuitWord");
    localStorage.removeItem("StudyReportCircuitId");
    localStorage.removeItem("internal-server-url");
    localStorage.removeItem("mobile-scann-api");
    localStorage.removeItem("can-see-no-all-rules");
    localStorage.removeItem("id");
    localStorage.removeItem("menus");
    localStorage.removeItem("see-draft-reservation-calendar");
    localStorage.removeItem("extaccionista-modality");
    localStorage.removeItem("see-patient-contact-info");
    localStorage.removeItem("see-close-with-totals");
    localStorage.removeItem("see-all-studies");
    localStorage.removeItem("sector-my-studies-checked");
    localStorage.removeItem("can-select-all-invoice-point");
    localStorage.removeItem("can-send-orderwhatsapp");
    localStorage.removeItem("can-no-validate-on-line");
    localStorage.removeItem("can-cancel-admited");
    localStorage.removeItem("can-publish-forced");
    localStorage.removeItem("can-view-other-centers-reception");
    localStorage.removeItem("can-update-like-particular");
    localStorage.removeItem("can-change-appointment-date");
    localStorage.removeItem("can-publish");
    localStorage.removeItem("medical-appointment-wait-notification");
    localStorage.removeItem("see-admission-valorization-amount");
    localStorage.removeItem("filter-physician-in-reservation");
    localStorage.removeItem("can-view-patient-other-profesional");
    localStorage.removeItem("settings-url");
    localStorage.removeItem("tx-control-url");
    localStorage.removeItem("can-change-password-autogesion");
    localStorage.removeItem("can-send-study-mail");
    localStorage.removeItem("change-pass");
    localStorage.removeItem("can-see-no-rules");
    localStorage.removeItem("SetSectorEquipment");
    localStorage.removeItem("control-admin-report");
    localStorage.removeItem("CanCloseInvoiceIntegrated");
    localStorage.removeItem("CanReservationReschedule");
    localStorage.removeItem("EnabledIA");
    localStorage.removeItem("EnableImportImageKeys");
    localStorage.removeItem("filter-billing-close");
    localStorage.removeItem("BillingCloseDate");
    this.scanner = null;
  }

  filterBillingClose(): boolean {
    return localStorage.getItem("filter-billing-close") === "1";
  }

  setFilterBillingClose(value: boolean): void {
    localStorage.setItem("filter-billing-close", value ? "1" : "0");
  }

  canChangePasswordAutogesion(): boolean {
    return localStorage.getItem("can-change-password-autogesion") === "1";
  }

  setSectorCenterCompaniesAndServices(
    sector: SectorFilter,
    center: Center,
    companies: CompanyFilter[],
    services: ServiceFilter[],
  ): void {
    this.loginUser.SectorSelected = sector;
    this.loginUser.CenterSelected = center;
    this.loginUser.CompaniesSelected = companies;

    const companyIds: number[] = [];
    if (companies) {
      companies.forEach((c) => {
        companyIds.push(c.Id);
      });
    }

    const serviceIds: number[] = [];
    if (services) {
      services.forEach((c) => {
        serviceIds.push(c.Id);
      });
    }

    localStorage.setItem("filter-billing-close", "0");

    if (this.loginUser.Token != null) {
      localStorage.setItem("pacs-name", this.loginUser.PacsName);
      localStorage.setItem("token", this.loginUser.Token);

      if (this.loginUser.ExpirationTokenMinutes == null) {
        localStorage.removeItem("token_expires_at");
      } else {
        const expiresAt =
          Date.now() + this.loginUser.ExpirationTokenMinutes * 60 * 1000;

        localStorage.setItem("token_expires_at", expiresAt.toString());
      }

      localStorage.setItem(
        "overMedicalAppointmentAuth",
        this.loginUser.OverMedicalAppointmentAuth ? "1" : "0",
      );

      localStorage.setItem(
        "SetSectorEquipment",
        this.loginUser.SetSectorEquipment ? "1" : "0",
      );

      localStorage.setItem(
        "sector",
        this.loginUser.SectorSelected ? this.loginUser.SectorSelected.Name : "",
      );
      localStorage.setItem(
        "center",
        this.loginUser.CenterSelected ? this.loginUser.CenterSelected.Name : "",
      );

      if (this.loginUser.CenterSelected) {
        localStorage.setItem(
          "ValidatePhoneRequired",
          this.loginUser.CenterSelected.ValidatePhoneRequired ? "1" : "0",
        );
      }

      let billingCloseDate = "";
      const billingCloseDateValue = this.loginUser.SectorSelected
        ? (this.loginUser.SectorSelected.BillingCloseDate as any)
        : null;

      if (billingCloseDateValue) {
        const billingDate = billingCloseDateValue instanceof Date
          ? billingCloseDateValue
          : new Date(billingCloseDateValue);

        billingCloseDate = !isNaN(billingDate.getTime())
          ? billingDate.toISOString()
          : String(billingCloseDateValue);
      }

      localStorage.setItem("BillingCloseDate", billingCloseDate);

      localStorage.setItem("can-test", this.loginUser.CanTest ? "1" : "0");
      localStorage.setItem("user-name", this.loginUser.UserName);
      localStorage.setItem("mobile-link-id", this.loginUser.MobileLinkId);
      localStorage.setItem("tx-control-url", this.loginUser.TxControlUrl);
      localStorage.setItem("environment", this.loginUser.Environment);
      localStorage.setItem("settings-url", this.loginUser.SettingsUrl);
      localStorage.setItem("first-name", this.loginUser.FirstName);
      localStorage.setItem("EnabledIA", this.loginUser.EnabledIA ? "1" : "0");
      localStorage.setItem(
        "EnableImportImageKeys",
        this.loginUser.EnableImportImageKeys ? "1" : "0",
      );
      localStorage.setItem("last-name", this.loginUser.LastName);
      localStorage.setItem("mobile-scann-api", this.loginUser.MobileScannApi);
      localStorage.setItem(
        "sector-my-studies-checked",
        sector.MySudiesChecked ? "1" : "0",
      );
      localStorage.setItem(
        "can-see-no-all-rules",
        this.loginUser.CanSeeNoAllRules ? "1" : "0",
      );
      localStorage.setItem(
        "internal-server-url",
        this.loginUser.InternalServerUrl,
      );
      const cardName =
        this.loginUser.FirstName.charAt(0).toUpperCase() +
        this.loginUser.LastName.charAt(0).toUpperCase();
      localStorage.setItem("card-name", cardName);
      localStorage.setItem(
        "extaccionista-modality",
        this.loginUser.IsExtaccionistaModality ? "1" : "0",
      );
      localStorage.setItem(
        "can-create-prescription",
        this.loginUser.CanCreatePrescription ? "1" : "0",
      );
      localStorage.setItem(
        "can-change-password-autogesion",
        this.loginUser.CanChangePasswordAutogesion ? "1" : "0",
      );
      localStorage.setItem(
        "can-see-no-rules",
        this.loginUser.CanSeeNoRules ? "1" : "0",
      );
      localStorage.setItem(
        "can-create-pending-reservation",
        this.loginUser.CanCreatePendingReservation ? "1" : "0",
      );
      localStorage.setItem(
        "can-change-reservation-sessions",
        this.loginUser.CanChangeReservationSessions ? "1" : "0",
      );
      localStorage.setItem(
        "control-admin-report",
        this.loginUser.ControlAdminReport ? "1" : "0",
      );
      localStorage.setItem(
        "can-send-orderwhatsapp",
        this.loginUser.CanSendOrderWhatsapp ? "1" : "0",
      );
      localStorage.setItem(
        "can-view-patient-other-profesional",
        this.loginUser.CanViewPatientOtherProfesional ? "1" : "0",
      );
      localStorage.setItem(
        "CanReservationReschedule",
        this.loginUser.CanReservationReschedule ? "1" : "0",
      );
      localStorage.setItem("sectorId", sector.Id.toString());
      localStorage.setItem("sector-type-id", sector.SectorTypeId.toString());
      localStorage.setItem(
        "sector-scann-document",
        sector.ScanDocument ? "1" : "0",
      );
      localStorage.setItem("centerId", center.Id.toString());
      localStorage.setItem("companyIds", JSON.stringify(companyIds));
      localStorage.setItem("serviceIds", JSON.stringify(serviceIds));
      localStorage.setItem("menus", JSON.stringify(this.loginUser.MenuTags));
      localStorage.setItem(
        "physicianId",
        this.loginUser.PhysicianId.toString(),
      );
      localStorage.setItem(
        "IsStudyReportCircuitWord",
        this.loginUser.IsStudyReportCircuitWord ? "1" : "0",
      );
      localStorage.setItem(
        "CanCloseInvoiceIntegrated",
        this.loginUser.CanCloseInvoiceIntegrated ? "1" : "0",
      );
      localStorage.setItem(
        "StudyReportCircuitId",
        this.loginUser.StudyReportCircuitId.toString(),
      );
      localStorage.setItem(
        "see-draft-reservation-calendar",
        this.loginUser.SeeDraftReservationCalendar ? "1" : "0",
      );
      localStorage.setItem(
        "see-patient-contact-info",
        this.loginUser.SeePatientContactInfo ? "1" : "0",
      );
      localStorage.setItem(
        "see-close-with-totals",
        this.loginUser.SeeCloseWithTotals ? "1" : "0",
      );

      localStorage.setItem(
        "see-admission-valorization-amount",
        this.loginUser.SeeAdmissionValorizationAmount ? "1" : "0",
      );
      localStorage.setItem(
        "see-all-studies",
        this.loginUser.CanSeeAllStudies ? "1" : "0",
      );
      localStorage.setItem(
        "can-select-all-invoice-point",
        this.loginUser.CanSelectAllInvoicePoint ? "1" : "0",
      );
      localStorage.setItem(
        "filter-physician-in-reservation",
        this.loginUser.FilterPhysicianInReservation ? "1" : "0",
      );
      localStorage.setItem(
        "can-no-validate-on-line",
        this.loginUser.CanSelectAllInvoicePoint ? "1" : "0",
      );
      localStorage.setItem(
        "can-cancel-admited",
        this.loginUser.CanCancelAdmited ? "1" : "0",
      );
      localStorage.setItem(
        "can-publish-forced",
        this.loginUser.CanPublishForced ? "1" : "0",
      );
      localStorage.setItem(
        "can-view-other-centers-reception",
        this.loginUser.CanViewOtherCentersReception ? "1" : "0",
      );
      localStorage.setItem(
        "can-update-like-particular",
        this.loginUser.CanUpdateLikeParticular ? "1" : "0",
      );
      localStorage.setItem(
        "can-change-appointment-date",
        this.loginUser.CanChangeAppointmentDate ? "1" : "0",
      );
      localStorage.setItem(
        "can-publish",
        this.loginUser.CanPublish ? "1" : "0",
      );
      localStorage.setItem(
        "can-send-study-mail",
        this.loginUser.CanSendStudyMail ? "1" : "0",
      );
      localStorage.setItem(
        "medical-appointment-wait-notification",
        this.loginUser.ReceiveMedicalAppointmentWaitPushNotification
          ? "1"
          : "0",
      );
      localStorage.setItem("id", this.loginUser.Id.toString());
    }
  }

  isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem("token_expires_at");
    if (!expiresAt) return false;

    return Date.now() > Number(expiresAt);
  }

  isTokenRefreshRequired(): boolean {
    const expiresAt = localStorage.getItem("token_expires_at");
    if (!expiresAt) return false;

    const expiresAtMs = Number(expiresAt);
    const remainingMs = expiresAtMs - Date.now();

    const tenMinutesMs = 10 * 60 * 1000;

    return remainingMs <= tenMinutesMs && remainingMs > 0;
  }

  isLoggedIn() {
    this.loggedIn =
      !!localStorage.getItem("token") && !!localStorage.getItem("sectorId");
    return this.loggedIn;
  }

  async isLoggedAndRefreshIfRequired(): Promise<boolean> {
    await this.refreshTokenIfRequired();
    return this.isLoggedIn();
  }

  setServerVersion(version: string) {
    this.serverVersion = version;
  }

  isOldVersion(): boolean {
    if (this.serverVersion) {
      return this.getVersionWeb() !== this.serverVersion;
    } else {
      return false;
    }
  }

  getVersion(): string {
    return environment.version;
  }

  getVersionWeb(): string {
    return environment.version_web;
  }

}
